import React from 'react';

/**
 * Format a byte count for display (GB when >= 1 GB, else MB).
 */
export const formatBytes = (bytes) => {
  if (!bytes || bytes < 0) return '0 MB';
  const gb = bytes / 1024 ** 3;
  if (gb >= 1) return `${gb.toFixed(gb >= 10 ? 0 : 1)} GB`;
  const mb = bytes / 1024 ** 2;
  return `${mb.toFixed(mb >= 10 ? 0 : 1)} MB`;
};

/**
 * A single usage row: label, usage / limit, and a progress bar.
 *
 * @param {string} label
 * @param {number} usage - current usage (bytes when isStorage)
 * @param {number|null} limit - null means unlimited (no bar, "Unlimited")
 * @param {number} percentage - 0-100 (from the limits API)
 * @param {boolean} [isStorage] - format usage/limit as bytes
 * @param {string} [note] - small qualifier appended to the label, e.g. "monthly"
 */
export default function UsageBar({ label, usage = 0, limit, percentage = 0, isStorage = false, note }) {
  const unlimited = limit === null || limit === undefined;
  const pct = unlimited ? 0 : Math.min(100, Math.max(0, percentage ?? 0));
  const near = pct >= 90;
  const usageText = isStorage ? formatBytes(usage) : usage;
  const limitText = unlimited ? 'Unlimited' : isStorage ? formatBytes(limit) : limit;

  return (
    <div>
      <div className="flex items-center justify-between mb-1 text-sm">
        <span className="text-[var(--text-main)]">
          {label}
          {note ? <span className="text-[var(--text-secondary)]"> ({note})</span> : null}
        </span>
        <span className="text-[var(--text-secondary)]">
          {usageText} / {limitText}
        </span>
      </div>
      {!unlimited && (
        <div className="h-2 rounded-full bg-[var(--bg-hover)] overflow-hidden theme-transition">
          {/* Color via a theme var-class; width must stay inline (dynamic %). */}
          <div
            className={`h-full rounded-full transition-all ${near ? 'bg-[var(--error)]' : 'bg-[var(--primary)]'}`}
            style={{ width: `${pct}%` }}
            role="progressbar"
            aria-label={label}
            aria-valuenow={Math.round(pct)}
            aria-valuemin={0}
            aria-valuemax={100}
          />
        </div>
      )}
    </div>
  );
}
